// IE 2026 10 18 / ODOO-347310: link to the record in the source system (AttrUrl) and product image by reference (AttrImageUrl).
// Pure helpers (no DOM, no store) — unit tests: tests/unit/sourceLink.test.mjs.
//
// Image references come from meta.source_image_url of the loaded record (a file in OUR S3 bucket:
// s3://bucket/key, https://<endpoint>/<bucket>/<key> or a bare key). The browser never reads S3 directly:
// the backend endpoint /api/object_image/ serves the file or a cached thumbnail under a stable URL.

export const IMAGE_ENDPOINT = '/api/object_image/'
export const THUMB_SIZES = [64, 128, 256, 512]

const HTML_ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }

export function escapeHtml(s) {
    return String(s ?? '').replace(/[&<>"']/g, c => HTML_ESC[c])
}

// only absolute http(s) URLs are links; anything else (javascript:, data:, relative) is not
export function safeHttpUrl(v) {
    if (typeof v !== 'string') return ''
    const s = v.trim()
    if (!/^https?:\/\/[^\s/?#]+/i.test(s)) return ''
    if (/[\s<>"'`]/.test(s)) return ''
    return s
}

// short label of a link: explicit text, else the host ("ee19-demo.datasynchub.com")
export function linkLabel(url, text) {
    if (text) return String(text)
    const m = /^https?:\/\/([^/?#]+)/i.exec(url || '')
    return m ? m[1] : ''
}

// HTML of a grid cell: a real <a target=_blank> so middle-click / copy link work as on any page
// IE 2026 10 19 / ODOO-347310 (ревью ie-webscm7): без нативного title — внутри .wj-control его не снимает nativeTitles, а подсказки только по Alt / Show tooltips
export function linkCellHtml(value, text) {
    const url = safeHttpUrl(value)
    if (!url) return ''
    const label = linkLabel(url, text)
    return `<a class="cell-source-link" href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">`
        + `<i class="pi pi-external-link"></i><span>${escapeHtml(label)}</span></a>`
}

// URL of the image endpoint for a stored reference; data:image/... is used as is
export function imageEndpointUrl(dbid, src, size) {
    if (typeof src !== 'string' || !src.trim()) return ''
    const s = src.trim()
    if (/^data:image\//i.test(s)) return s
    if (s.startsWith(IMAGE_ENDPOINT)) return s
    let url = `${IMAGE_ENDPOINT}?db=${encodeURIComponent(dbid || '')}&src=${encodeURIComponent(s)}`
    if (size) {
        const n = THUMB_SIZES.find(x => x >= +size) || THUMB_SIZES[THUMB_SIZES.length - 1]
        url += `&size=${n}`
    }
    return url
}

// thumbnail size for a cell of the given height (devicePixelRatio 2 → twice the pixels)
export function thumbSizeFor(px, dpr = 1) {
    const need = Math.ceil((+px || 32) * (dpr || 1))
    return THUMB_SIZES.find(x => x >= need) || THUMB_SIZES[THUMB_SIZES.length - 1]
}

// Odoo moved the web client to /odoo/<model>/<id> in 17.2 (saas); 18.0 is the first major with it. Same rule as BE common_modules/source_link.py
export const ODOO_PATH_ROUTE_MIN_MAJOR = 18

// URL of an Odoo record form; version unknown → the modern route
export function odooRecordUrl(baseUrl, model, recId, version) {
    const base = String(baseUrl || '').trim().replace(/\/+$/, '')
    const m = String(model || '').trim()
    const id = String(recId ?? '').trim()
    if (!safeHttpUrl(base) || !/^[a-z0-9_]+(\.[a-z0-9_]+)*$/.test(m) || !/^\d+$/.test(id)) return ''
    const major = parseInt((String(version ?? '').match(/\d+/) || [])[0], 10)
    if (!Number.isNaN(major) && major < ODOO_PATH_ROUTE_MIN_MAJOR) return `${base}/web#model=${m}&id=${id}&view_type=form`
    return `${base}/odoo/${m}/${id}`
}

// "open in source" link of a loaded record: meta.source_url, else built from the parts (Odoo only)
export function sourceUrlFromMeta(meta) {
    if (!meta || typeof meta !== 'object') return ''
    const direct = safeHttpUrl(meta.source_url)
    if (direct) return direct
    if (String(meta.source_system || 'odoo').toLowerCase() !== 'odoo') return ''
    return odooRecordUrl(meta.source_base_url, meta.source_model, meta.source_id, meta.source_version)
}
